import { useState, useRef, useCallback, useEffect, RefObject } from "react";

const HIDE_DELAY_MS = 150;
// Primary mouse button is the left mouse button
const PRIMARY_MOUSE_BUTTON = 0;

const hasSelectionInElement = (element: HTMLElement | null): boolean => {
  if (!element) return false;

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  const selectedText = selection.toString();
  if (selectedText.length === 0) return false;

  const range = selection.getRangeAt(0);
  return element.contains(range.commonAncestorContainer);
};

export function useCopyCodeButtonScroll(
  preRef: RefObject<HTMLPreElement | null>,
) {
  const [isHidden, setIsHidden] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Hide button temporarily (with auto-show after timeout) - used for scroll/wheel
  const hideButtonByTimeout = useCallback(() => {
    setIsHidden(true);
    clearHideTimeout();
    timeoutRef.current = setTimeout(() => {
      setIsHidden(false);
    }, HIDE_DELAY_MS);
  }, [clearHideTimeout]);

  const hideButton = useCallback(() => {
    clearHideTimeout();
    setIsHidden(true);
  }, [clearHideTimeout]);

  const showButton = useCallback(() => {
    clearHideTimeout();
    setIsHidden(false);
  }, [clearHideTimeout]);

  // Hide button while user is actively selecting inside the code block,
  // and show it again once the mouse is released. Uses requestAnimationFrame
  // to avoid interfering with the browser's selection logic.
  useEffect(() => {
    const preElement = preRef.current;
    if (!preElement) return;

    const handleMouseDown = (event: MouseEvent) => {
      if (event.button !== PRIMARY_MOUSE_BUTTON) return;
      hideButton();
    };

    const handleMouseUp = () => {
      const hasSelection = hasSelectionInElement(preElement);

      if (hasSelection) {
        // Selection finished inside the code block: keep the selection,
        // but show the button again on the next frame to avoid interfering
        // with the browser's selection logic.
        requestAnimationFrame(() => {
          showButton();
        });
        return;
      }

      // No selection (or selection outside the code block) → show the button
      showButton();
    };

    preElement.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      preElement.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
      clearHideTimeout();
    };
  }, [hideButton, showButton, preRef, clearHideTimeout]);

  return {
    isHidden,
    hideButton: hideButtonByTimeout, // Export the timeout version for scroll/wheel
    showButton,
  };
}
